import { useState, memo, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
  Tooltip,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  SaveAlt as SaveTemplateIcon,
  Info as InfoIcon,
  Send as SendIcon,
  Cancel as CancelIcon,
  CheckCircle as AcceptedIcon,
  Schedule as PendingIcon,
  Error as ExpiredIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { Board, PayoutSchemeConfig, UpdateBoardInput, CreateTemplateFromBoardInput, ThemeWithOwnership, BoardInvite } from '../../types';
import { getBoxNeighborCount } from '../../types';
import type { BoardBasicInfo } from '../BoardSettingsForm';
import { BoardSettingsForm } from '../BoardSettingsForm';
import { PayoutConfigEditor } from '../PayoutConfigEditor';
import { GameDateTimePicker } from '../GameDateTimePicker';
import { templatesApi, boardsApi } from '../../api';
import { ThemeSelector } from '../theme';
import { useBoardTheme } from '../../hooks';
import { ConfirmDialog } from '../shared/ConfirmDialog';

// Helper function for clipboard operations with fallback
const copyToClipboard = async (text: string): Promise<boolean> => {
  // Try modern clipboard API first
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn('Clipboard API failed, trying fallback:', err);
    }
  }

  // Fallback for non-secure contexts or when clipboard API fails
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    // Make textarea visible but off-screen to ensure it can receive focus
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    textArea.style.opacity = '0';
    textArea.style.zIndex = '99999';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, text.length); // For mobile devices
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
};

// Helper to detect if quarterPayouts are in percentage format (legacy) or dollar format (new)
// Percentages are typically between 0 and 1 (e.g., 0.25 = 25%)
// Dollar amounts are typically > 1 (e.g., $250)
const isPercentageFormat = (quarterPayouts: Record<string, number>): boolean => {
  const values = Object.values(quarterPayouts);
  if (values.length === 0) return false;
  // If all values are <= 1, likely percentages
  return values.every(v => v <= 1);
};

// Convert percentage-based payouts to dollar amounts
const convertPercentagesToDollars = (
  percentages: Record<string, number>,
  payoutPot: number
): Record<string, number> => {
  const dollars: Record<string, number> = {};
  for (const [key, pct] of Object.entries(percentages)) {
    dollars[key] = Math.round(payoutPot * pct * 100) / 100;
  }
  return dollars;
};

export interface DuplicateBoardInput {
  name: string;
  slug: string;
  homeTeam: string;
  awayTeam: string;
  homeColor: string;
  awayColor: string;
  gameDate: string;
  copySquares: boolean;
}

export interface SettingsPanelProps {
  board: Board;
  readOnly?: boolean;
  onSavePayoutConfig: (config: PayoutSchemeConfig) => Promise<void>;
  onSaveBasicInfo: (input: UpdateBoardInput) => Promise<void>;
  onDuplicate: (input: DuplicateBoardInput) => Promise<void>;
  onDelete: () => Promise<void>;
  onThemePurchase?: (theme: ThemeWithOwnership) => void;
  onBoardUpdated?: () => void;
}

export const SettingsPanel = memo<SettingsPanelProps>(({
  board,
  readOnly = false,
  onSavePayoutConfig,
  onSaveBasicInfo,
  onDuplicate,
  onDelete,
  onThemePurchase,
  onBoardUpdated,
}) => {
  // Theme management
  const { applyTheme, removeTheme, isApplying, isRemoving, error: themeError } = useBoardTheme(board.id);

  const handleThemeSelect = async (themeId: string | null) => {
    try {
      if (themeId) {
        await applyTheme(themeId);
      } else {
        await removeTheme();
      }
      onBoardUpdated?.();
    } catch {
      // Error is handled by the hook
    }
  };

  // Invite management state
  const [invites, setInvites] = useState<BoardInvite[]>([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [invitesError, setInvitesError] = useState<string | null>(null);
  const [resendingInvite, setResendingInvite] = useState<string | null>(null);
  const [cancellingInvite, setCancellingInvite] = useState<string | null>(null);
  const [resendConfirmInvite, setResendConfirmInvite] = useState<BoardInvite | null>(null);
  const [cancelConfirmInvite, setCancelConfirmInvite] = useState<BoardInvite | null>(null);

  // Load invites on mount
  const loadInvites = useCallback(async () => {
    setInvitesLoading(true);
    setInvitesError(null);
    try {
      const data = await boardsApi.getInvites(board.id);
      setInvites(data);
    } catch (err) {
      setInvitesError(err instanceof Error ? err.message : 'Failed to load invites');
    } finally {
      setInvitesLoading(false);
    }
  }, [board.id]);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  const handleResendInvite = async (invite: BoardInvite, forceResend = false) => {
    setResendingInvite(invite.id);
    try {
      await boardsApi.resendInvite(board.id, invite.id, forceResend);
      await loadInvites();
      setResendConfirmInvite(null);
    } catch (err) {
      if (err instanceof Error && err.message.includes('rate limit')) {
        // Show confirmation dialog for force resend
        setResendConfirmInvite(invite);
      } else {
        setInvitesError(err instanceof Error ? err.message : 'Failed to resend invite');
      }
    } finally {
      setResendingInvite(null);
    }
  };

  const handleCancelInvite = async (invite: BoardInvite) => {
    setCancellingInvite(invite.id);
    try {
      await boardsApi.cancelInvite(board.id, invite.id);
      await loadInvites();
      setCancelConfirmInvite(null);
    } catch (err) {
      setInvitesError(err instanceof Error ? err.message : 'Failed to cancel invite');
    } finally {
      setCancellingInvite(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusChip = (status: BoardInvite['status']) => {
    switch (status) {
      case 'accepted':
        return <Chip label="Accepted" size="small" color="success" icon={<AcceptedIcon />} />;
      case 'expired':
        return <Chip label="Expired" size="small" color="error" icon={<ExpiredIcon />} />;
      default:
        return <Chip label="Pending" size="small" color="warning" icon={<PendingIcon />} />;
    }
  };

  // Basic info state
  const [basicInfo, setBasicInfo] = useState<BoardBasicInfo>({
    name: board.name,
    slug: board.slug || '',
    description: board.description || '',
    sport: board.sport as BoardBasicInfo['sport'],
    homeTeam: board.homeTeam,
    awayTeam: board.awayTeam,
    homeColor: board.homeColor || '#1976d2',
    awayColor: board.awayColor || '#d32f2f',
    gameDate: board.gameDate,
    pricePerSquare: board.pricePerSquare,
    quarters: board.quarters || 4,
    numberMode: board.numberMode || 'single',
    cellMode: board.cellMode || 'standard',
    cellShape: board.cellShape || '1x1',
  });
  const [hasBasicInfoChanges, setHasBasicInfoChanges] = useState(false);

  // Payout config state - convert legacy percentage format to dollars if needed
  const initialPayoutConfig = useMemo(() => {
    const config = board.payoutConfig;
    const profit = config.profitPercent ?? 10;
    const totalCells = board.totalCells || 100;
    const totalPot = board.pricePerSquare * totalCells;
    const payoutPot = totalPot * (1 - profit / 100);

    // Check if the data is in legacy percentage format
    if (isPercentageFormat(config.quarterPayouts)) {
      return {
        ...config,
        quarterPayouts: convertPercentagesToDollars(config.quarterPayouts, payoutPot),
      };
    }
    return config;
  }, [board.payoutConfig, board.pricePerSquare]);

  const [payoutConfig, setPayoutConfig] = useState<PayoutSchemeConfig>(initialPayoutConfig);
  const [profitPercent, setProfitPercent] = useState(board.payoutConfig.profitPercent ?? 10);
  const [pricePerSquare, setPricePerSquare] = useState(board.pricePerSquare);
  const [hasPayoutChanges, setHasPayoutChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [slugCopied, setSlugCopied] = useState(false);

  // Compute payout validation status with details
  const payoutValidation = useMemo(() => {
    const totalCells = board.totalCells || 100;
    const totalPot = pricePerSquare * totalCells;
    const profitAmount = totalPot * (profitPercent / 100);
    const payoutPot = totalPot - profitAmount;

    // Calculate total allocated dollars
    const quarterTotal = Object.values(payoutConfig.quarterPayouts).reduce((sum, amount) => sum + amount, 0);
    const wrongWayTotal = Object.values(payoutConfig.quarterWrongWayPayouts || {}).reduce((sum, amount) => sum + amount, 0);
    const plusNTotal = Object.values(payoutConfig.quarterPlusNPayouts || {}).reduce((sum, amount) => sum + amount, 0);
    const plusNWrongWayTotal = Object.values(payoutConfig.quarterPlusNWrongWayPayouts || {}).reduce((sum, amount) => sum + amount, 0);
    // Custom categories: if wrongWayEnabled, payout is doubled
    const customTotal = (payoutConfig.customCategories || []).reduce((sum, cat) => {
      const baseAmount = cat.payoutAmount || 0;
      return sum + (cat.wrongWayEnabled ? baseAmount * 2 : baseAmount);
    }, 0);

    // Neighbor payouts calculation
    const neighborConfig = payoutConfig.neighborPayouts;
    const neighborDirections = neighborConfig?.directions || [];
    const quarters = board.quarters || 4;
    const neighborTotal = (() => {
      if (!neighborConfig?.enabled) return 0;
      const directionCount = neighborDirections.length || 0;
      // Get quarter count for calculation
      const quarterKeys = quarters === 2 ? ['1H', '2H'] : quarters === 3 ? ['P1', 'P2', 'P3'] : ['Q1', 'Q2', 'Q3', 'Q4'];
      if (neighborConfig.usePerQuarterAmounts && neighborConfig.quarterAmounts) {
        return quarterKeys.reduce(
          (sum, key) => sum + (neighborConfig.quarterAmounts?.[key] || 0) * directionCount,
          0
        );
      }
      return (neighborConfig.globalAmount || 0) * directionCount * quarterKeys.length;
    })();

    // Bad numbers payouts calculation
    const badNumbersConfig = payoutConfig.badNumbersConfig;
    const badNumbersTotal = (() => {
      if (!badNumbersConfig?.enabled) return 0;
      // Use badCombinations if available, otherwise fall back to legacy badDigits
      if (badNumbersConfig.badCombinations && badNumbersConfig.badCombinations.length > 0) {
        return badNumbersConfig.badCombinations.length * (badNumbersConfig.payoutAmount || 0);
      }
      // Legacy fallback
      const badDigitCount = badNumbersConfig.badDigits?.length || 0;
      const badSquareCount = badDigitCount * badDigitCount;
      return badSquareCount * (badNumbersConfig.payoutAmount || 0);
    })();

    // Box neighbor payouts calculation
    const boxNeighborTotal = (() => {
      const boxConfig = payoutConfig.boxNeighborPayouts;
      if (!boxConfig?.enabled) return 0;
      const boxNeighborCount = getBoxNeighborCount(boxConfig.boxSize);
      const quarterKeys = quarters === 2 ? ['1H', '2H'] : quarters === 3 ? ['P1', 'P2', 'P3'] : ['Q1', 'Q2', 'Q3', 'Q4'];
      if (boxConfig.usePerQuarterAmounts && boxConfig.quarterAmounts) {
        return quarterKeys.reduce(
          (sum, key) => sum + (boxConfig.quarterAmounts?.[key] || 0) * boxNeighborCount,
          0
        );
      }
      return (boxConfig.globalAmount || 0) * boxNeighborCount * quarterKeys.length;
    })();

    const allocatedDollars = quarterTotal + wrongWayTotal + plusNTotal + plusNWrongWayTotal + customTotal + neighborTotal + badNumbersTotal + boxNeighborTotal;
    const remainingDollars = payoutPot - allocatedDollars;
    const isValid = Math.abs(remainingDollars) <= 0.01;

    return {
      isValid,
      remainingDollars,
      isOverage: remainingDollars < -0.01,
      payoutPot,
      allocatedDollars,
    };
  }, [pricePerSquare, profitPercent, payoutConfig, board.quarters]);

  const isPayoutConfigValid = payoutValidation.isValid;

  // Duplicate board state
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateName, setDuplicateName] = useState('');
  const [duplicateSlug, setDuplicateSlug] = useState('');
  const [duplicateHomeTeam, setDuplicateHomeTeam] = useState(board.homeTeam);
  const [duplicateAwayTeam, setDuplicateAwayTeam] = useState(board.awayTeam);
  const [duplicateHomeColor, setDuplicateHomeColor] = useState(board.homeColor || '#1976d2');
  const [duplicateAwayColor, setDuplicateAwayColor] = useState(board.awayColor || '#d32f2f');
  const [duplicateGameDate, setDuplicateGameDate] = useState('');
  const [duplicateCopySquares, setDuplicateCopySquares] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);

  // Delete board state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Save as template state
  const navigate = useNavigate();
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateIncludeAssignments, setTemplateIncludeAssignments] = useState(true);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  // Payout settings are read-only once board is published
  const isPayoutReadOnly = board.status !== 'draft';

  // Handle basic info changes
  const handleBasicInfoChange = (newInfo: BoardBasicInfo) => {
    setBasicInfo(newInfo);
    setHasBasicInfoChanges(true);
  };

  const handleSaveBasicInfo = async () => {
    setSaving(true);
    try {
      await onSaveBasicInfo({
        name: basicInfo.name,
        description: basicInfo.description,
        homeTeam: basicInfo.homeTeam,
        awayTeam: basicInfo.awayTeam,
        homeColor: basicInfo.homeColor,
        awayColor: basicInfo.awayColor,
        numberMode: basicInfo.numberMode,
        cellMode: basicInfo.cellMode,
        cellShape: basicInfo.cellShape,
      });
      setHasBasicInfoChanges(false);
    } finally {
      setSaving(false);
    }
  };

  const handleResetBasicInfo = () => {
    setBasicInfo({
      name: board.name,
      slug: board.slug || '',
      description: board.description || '',
      sport: board.sport as BoardBasicInfo['sport'],
      homeTeam: board.homeTeam,
      awayTeam: board.awayTeam,
      homeColor: board.homeColor || '#1976d2',
      awayColor: board.awayColor || '#d32f2f',
      gameDate: board.gameDate,
      pricePerSquare: board.pricePerSquare,
      quarters: board.quarters || 4,
      numberMode: board.numberMode || 'single',
      cellMode: board.cellMode || 'standard',
      cellShape: board.cellShape || '1x1',
    });
    setHasBasicInfoChanges(false);
  };

  // Handle payout config changes
  const handlePayoutConfigChange = (config: PayoutSchemeConfig) => {
    setPayoutConfig(config);
    setHasPayoutChanges(true);
  };

  const handleProfitPercentChange = (percent: number) => {
    setProfitPercent(percent);
    setHasPayoutChanges(true);
  };

  const handlePricePerSquareChange = (price: number) => {
    setPricePerSquare(price);
    setHasPayoutChanges(true);
  };

  const handleSavePayoutConfig = async () => {
    setSaving(true);
    try {
      // Save price per square if changed
      if (pricePerSquare !== board.pricePerSquare) {
        await onSaveBasicInfo({ pricePerSquare });
      }
      // Save payout config
      await onSavePayoutConfig({
        ...payoutConfig,
        profitPercent,
      });
      setHasPayoutChanges(false);
    } finally {
      setSaving(false);
    }
  };

  const handleResetPayout = () => {
    setPayoutConfig(initialPayoutConfig);
    setProfitPercent(board.payoutConfig.profitPercent ?? 10);
    setPricePerSquare(board.pricePerSquare);
    setHasPayoutChanges(false);
  };

  const handleOpenDuplicateDialog = () => {
    setDuplicateName(`${board.name} (Copy)`);
    setDuplicateSlug(board.slug ? `${board.slug}-copy` : '');
    setDuplicateHomeTeam(board.homeTeam);
    setDuplicateAwayTeam(board.awayTeam);
    setDuplicateHomeColor(board.homeColor || '#1976d2');
    setDuplicateAwayColor(board.awayColor || '#d32f2f');
    // Default game date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setDuplicateGameDate(tomorrow.toISOString().slice(0, 16));
    setDuplicateCopySquares(true);
    setDuplicateError(null);
    setDuplicateDialogOpen(true);
  };

  const handleCloseDuplicateDialog = () => {
    setDuplicateDialogOpen(false);
    setDuplicateError(null);
  };

  const handleDuplicate = async () => {
    if (!duplicateName.trim()) {
      setDuplicateError('Board name is required');
      return;
    }
    if (!duplicateHomeTeam.trim() || !duplicateAwayTeam.trim()) {
      setDuplicateError('Both team names are required');
      return;
    }
    if (!duplicateGameDate) {
      setDuplicateError('Game date is required');
      return;
    }

    setDuplicating(true);
    setDuplicateError(null);
    try {
      await onDuplicate({
        name: duplicateName.trim(),
        slug: duplicateSlug.trim(),
        homeTeam: duplicateHomeTeam.trim(),
        awayTeam: duplicateAwayTeam.trim(),
        homeColor: duplicateHomeColor,
        awayColor: duplicateAwayColor,
        gameDate: new Date(duplicateGameDate).toISOString(),
        copySquares: duplicateCopySquares,
      });
      handleCloseDuplicateDialog();
    } catch (err) {
      setDuplicateError(err instanceof Error ? err.message : 'Failed to duplicate board');
    } finally {
      setDuplicating(false);
    }
  };

  const handleOpenDeleteDialog = () => {
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteError(null);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete board');
    } finally {
      setDeleting(false);
    }
  };

  // Save as template handlers
  const handleOpenTemplateDialog = () => {
    setTemplateName(`${board.name} Template`);
    setTemplateDescription('');
    setTemplateIncludeAssignments(true);
    setTemplateError(null);
    setTemplateDialogOpen(true);
  };

  const handleCloseTemplateDialog = () => {
    setTemplateDialogOpen(false);
    setTemplateError(null);
  };

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) {
      setTemplateError('Template name is required');
      return;
    }

    setSavingTemplate(true);
    setTemplateError(null);
    try {
      const input: CreateTemplateFromBoardInput = {
        name: templateName.trim(),
        description: templateDescription.trim() || undefined,
        includeAssignments: templateIncludeAssignments,
      };
      const newTemplate = await templatesApi.createFromBoard(board.id, input);
      handleCloseTemplateDialog();
      // Navigate to the new template
      navigate(`/profile/templates/${newTemplate.id}`);
    } catch (err) {
      setTemplateError(err instanceof Error ? err.message : 'Failed to create template');
    } finally {
      setSavingTemplate(false);
    }
  };

  return (
    <Box sx={readOnly ? { pointerEvents: 'none', opacity: 0.9 } : undefined}>
      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You are viewing this board's settings in read-only mode.
        </Alert>
      )}
      {/* Board Settings Form - Description & Teams */}
      <Typography variant="h6" gutterBottom>
        Board Details
      </Typography>
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Board Title */}
        <TextField
          label="Board Title"
          fullWidth
          value={basicInfo.name}
          onChange={(e) => handleBasicInfoChange({ ...basicInfo, name: e.target.value })}
          sx={{ mb: 2 }}
        />
        {/* Board Slug (read-only display with full URL and copy button) */}
        {board.slug && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              URL:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                bgcolor: 'grey.100',
                px: 1,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              {window.location.origin}/boards/{board.slug}
            </Typography>
            <Tooltip title={slugCopied ? 'Copied!' : 'Copy URL'}>
              <IconButton
                size="small"
                onClick={async () => {
                  const success = await copyToClipboard(`${window.location.origin}/boards/${board.slug}`);
                  if (success) {
                    setSlugCopied(true);
                    setTimeout(() => setSlugCopied(false), 2000);
                  }
                }}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <BoardSettingsForm
          values={basicInfo}
          onChange={handleBasicInfoChange}
          mode="edit"
          boardStatus={board.status}
          showName={false}
          showSport={false}
          showQuarters={false}
          showGameDate={false}
          showPricePerSquare={false}
          showTeamColors={!board.themeId}
          showNumberMode={false}
          isLiveGame={!!board.externalGameId}
        />

        {/* Read-only Number Sets display */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Number Sets:
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {board.numberMode === 'quarter'
                ? 'Different per Quarter'
                : board.numberMode === 'half'
                  ? 'Different per Half'
                  : 'Same for All Periods'}
            </Typography>
            <Tooltip title="Number mode is set during board creation and cannot be changed after the board is created.">
              <InfoIcon fontSize="small" color="action" sx={{ ml: 0.5 }} />
            </Tooltip>
          </Box>
        </Box>

        {!readOnly && <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSaveBasicInfo}
            disabled={saving || !hasBasicInfoChanges}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          {hasBasicInfoChanges && (
            <Button variant="outlined" onClick={handleResetBasicInfo} disabled={saving}>
              Reset
            </Button>
          )}
        </Box>}
      </Paper>

      {/* Board Theme Section */}
      <Typography variant="h6" gutterBottom>
        Board Theme
      </Typography>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Apply a visual theme to customize how your board looks. Themes affect both the live board view and exported images.
          {board.themeId && ' When a theme is applied, its colors are used for the team labels.'}
        </Typography>
        {themeError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {themeError}
          </Alert>
        )}
        <ThemeSelector
          selectedThemeId={board.themeId}
          onSelect={handleThemeSelect}
          onPurchaseClick={onThemePurchase}
          showPurchaseButton={!!onThemePurchase}
          disabled={isApplying || isRemoving}
        />
        {(isApplying || isRemoving) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              {isApplying ? 'Applying theme...' : 'Removing theme...'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Email Invites Section */}
      <Typography variant="h6" gutterBottom>
        Email Invites
      </Typography>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Track email invitations sent to this board. Rate limit: 3 emails per person per 24 hours.
        </Typography>

        {invitesError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setInvitesError(null)}>
            {invitesError}
          </Alert>
        )}

        {invitesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : invites.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No email invitations sent yet. Use the "Invite Player" button to send invites.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Sent</TableCell>
                  <TableCell align="center">Times Sent</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invites.map((invite) => (
                  <TableRow key={invite.id}>
                    <TableCell>
                      <Typography variant="body2">{invite.invitedEmail}</Typography>
                      {invite.invitedByName && (
                        <Typography variant="caption" color="text.secondary">
                          by {invite.invitedByName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{getStatusChip(invite.status)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(invite.lastSentAt)}</Typography>
                      {invite.status === 'accepted' && invite.acceptedAt && (
                        <Typography variant="caption" color="text.secondary">
                          Accepted: {formatDate(invite.acceptedAt)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={invite.sendCount} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      {invite.status === 'pending' && (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Tooltip title="Resend invite email">
                            <IconButton
                              size="small"
                              onClick={() => handleResendInvite(invite)}
                              disabled={resendingInvite === invite.id}
                            >
                              {resendingInvite === invite.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <SendIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel invite">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setCancelConfirmInvite(invite)}
                              disabled={cancellingInvite === invite.id}
                            >
                              {cancellingInvite === invite.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <CancelIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Rate Limit Confirmation Dialog */}
      <ConfirmDialog
        open={!!resendConfirmInvite}
        title="Rate Limit Reached"
        message={`You've sent ${resendConfirmInvite?.sendCount || 0} invites to ${resendConfirmInvite?.invitedEmail} in the last 24 hours. Are you sure you want to send another?`}
        confirmLabel="Send Anyway"
        cancelLabel="Cancel"
        icon="warning"
        onConfirm={() => { if (resendConfirmInvite) handleResendInvite(resendConfirmInvite, true); }}
        onCancel={() => setResendConfirmInvite(null)}
      />

      {/* Cancel Invite Confirmation Dialog */}
      <ConfirmDialog
        open={!!cancelConfirmInvite}
        title="Cancel Invite"
        message={`Are you sure you want to cancel the invite to ${cancelConfirmInvite?.invitedEmail}? They won't be able to join using this invite link.`}
        confirmLabel="Cancel Invite"
        cancelLabel="Keep Invite"
        confirmColor="error"
        onConfirm={() => { if (cancelConfirmInvite) handleCancelInvite(cancelConfirmInvite); }}
        onCancel={() => setCancelConfirmInvite(null)}
      />

      {/* Payout Configuration Section */}
      <Typography variant="h6" gutterBottom>
        Payout Configuration
      </Typography>
      {isPayoutReadOnly ? (
        <Paper sx={{ p: 3, mb: 3, overflow: 'hidden' }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Payout settings cannot be changed after the board is published.
          </Alert>
          <PayoutConfigEditor
            payoutConfig={payoutConfig}
            onPayoutConfigChange={() => {}}
            pricePerSquare={pricePerSquare}
            profitPercent={profitPercent}
            onProfitPercentChange={() => {}}
            quarters={board.quarters || 4}
            totalCells={board.totalCells || 100}
            readOnly
          />
        </Paper>
      ) : (
        <>
          <PayoutConfigEditor
            payoutConfig={payoutConfig}
            onPayoutConfigChange={handlePayoutConfigChange}
            pricePerSquare={pricePerSquare}
            onPricePerSquareChange={handlePricePerSquareChange}
            profitPercent={profitPercent}
            onProfitPercentChange={handleProfitPercentChange}
            quarters={board.quarters || 4}
            totalCells={board.totalCells || 100}
          />
          {/* Validation warning - always show when invalid */}
          {!isPayoutConfigValid && (
            <Alert
              severity="error"
              icon={<WarningIcon />}
              sx={{ mt: 2, mb: 1 }}
            >
              {payoutValidation.isOverage
                ? `Payouts exceed the payout pot by $${Math.abs(payoutValidation.remainingDollars).toFixed(2)}. Reduce payout amounts to save.`
                : `$${payoutValidation.remainingDollars.toFixed(2)} unallocated. Allocate all funds to save.`
              }
            </Alert>
          )}
          {!readOnly && <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
            <Tooltip
              title={
                !isPayoutConfigValid
                  ? (payoutValidation.isOverage
                      ? `Cannot save: $${Math.abs(payoutValidation.remainingDollars).toFixed(2)} over budget`
                      : `Cannot save: $${payoutValidation.remainingDollars.toFixed(2)} unallocated`)
                  : (!hasPayoutChanges ? 'No changes to save' : '')
              }
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleSavePayoutConfig}
                  disabled={saving || !hasPayoutChanges || !isPayoutConfigValid}
                >
                  {saving ? 'Saving...' : 'Save Payout Settings'}
                </Button>
              </span>
            </Tooltip>
            {hasPayoutChanges && (
              <Button variant="outlined" onClick={handleResetPayout} disabled={saving}>
                Reset
              </Button>
            )}
          </Box>}
        </>
      )}

      {/* Duplicate Board Section */}
      {!readOnly && <><Divider sx={{ my: 4 }} />
      <Typography variant="h6" gutterBottom>
        Duplicate Board
      </Typography>
      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create a copy of this board with the same players (online and offline). The new board will start as a draft with no scores, square claims, or payment history.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<CopyIcon />}
          onClick={handleOpenDuplicateDialog}
        >
          Duplicate Board
        </Button>
      </Paper>

      {/* Duplicate Board Dialog */}
      <Dialog open={duplicateDialogOpen} onClose={handleCloseDuplicateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Duplicate Board</DialogTitle>
        <DialogContent>
          {duplicateError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {duplicateError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Board Name"
            fullWidth
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="URL Slug (optional)"
            fullWidth
            value={duplicateSlug}
            onChange={(e) => setDuplicateSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="e.g., super-bowl-2025"
            helperText="3-50 lowercase letters, numbers, and hyphens. Leave empty for auto-generated URL."
            sx={{ mb: 2 }}
          />
          <GameDateTimePicker
            value={duplicateGameDate}
            onChange={setDuplicateGameDate}
            required
          />

          {/* Team A Row */}
          <Typography variant="subtitle2" gutterBottom>
            Teams
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                border: '2px solid #ccc',
                backgroundColor: duplicateHomeColor,
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <input
                type="color"
                value={duplicateHomeColor}
                onChange={(e) => setDuplicateHomeColor(e.target.value)}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer',
                }}
                title="Team A color"
              />
            </Box>
            <TextField
              label="Team A"
              value={duplicateHomeTeam}
              onChange={(e) => setDuplicateHomeTeam(e.target.value)}
              size="small"
              sx={{ flexGrow: 1 }}
            />
          </Box>

          {/* Team B Row */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                border: '2px solid #ccc',
                backgroundColor: duplicateAwayColor,
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <input
                type="color"
                value={duplicateAwayColor}
                onChange={(e) => setDuplicateAwayColor(e.target.value)}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer',
                }}
                title="Team B color"
              />
            </Box>
            <TextField
              label="Team B"
              value={duplicateAwayTeam}
              onChange={(e) => setDuplicateAwayTeam(e.target.value)}
              size="small"
              sx={{ flexGrow: 1 }}
            />
          </Box>

          {/* Copy Squares Toggle */}
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={duplicateCopySquares}
                onChange={(e) => setDuplicateCopySquares(e.target.checked)}
              />
            }
            label="Copy square claims/positions"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
            If enabled, all square claims will be copied to the new board. Otherwise, the board will start with no squares claimed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDuplicateDialog} disabled={duplicating}>
            Cancel
          </Button>
          <Button
            onClick={handleDuplicate}
            variant="contained"
            disabled={duplicating}
            startIcon={duplicating ? <CircularProgress size={16} /> : <CopyIcon />}
          >
            {duplicating ? 'Duplicating...' : 'Create Copy'}
          </Button>
        </DialogActions>
      </Dialog></>}

      {/* Save as Template Section */}
      {!readOnly && <><Divider sx={{ my: 4 }} />
      <Typography variant="h6" gutterBottom>
        Save as Template
      </Typography>
      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Save this board's settings and offline player assignments as a reusable template.
          Create new boards quickly using templates from your Profile.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<SaveTemplateIcon />}
          onClick={handleOpenTemplateDialog}
        >
          Save as Template
        </Button>
      </Paper>

      {/* Save as Template Dialog */}
      <Dialog open={templateDialogOpen} onClose={handleCloseTemplateDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Save as Template</DialogTitle>
        <DialogContent>
          {templateError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {templateError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            fullWidth
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={2}
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.target.value.slice(0, 250))}
            helperText={`${templateDescription.length}/250 characters`}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={templateIncludeAssignments}
                onChange={(e) => setTemplateIncludeAssignments(e.target.checked)}
              />
            }
            label="Include offline player assignments"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
            If enabled, offline player square positions will be saved in the template and applied when creating new boards.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTemplateDialog} disabled={savingTemplate}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAsTemplate}
            variant="contained"
            disabled={savingTemplate || !templateName.trim()}
            startIcon={savingTemplate ? <CircularProgress size={16} /> : <SaveTemplateIcon />}
          >
            {savingTemplate ? 'Saving...' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog></>}

      {/* Delete Board Section - only for draft boards */}
      {!readOnly && board.status === 'draft' && (
        <>
          <Divider sx={{ my: 4 }} />
          <Typography variant="h6" gutterBottom color="error">
            Danger Zone
          </Typography>
          <Paper sx={{ p: 3, border: '1px solid', borderColor: 'error.main', bgcolor: 'error.50' }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Deleting a board is permanent and cannot be undone. All data including squares, players, and settings will be lost.
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This action will permanently delete the board "{board.name}" and all associated data.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleOpenDeleteDialog}
            >
              Delete Board
            </Button>
          </Paper>
        </>
      )}

      {/* Delete Board Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Board?</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to permanently delete <strong>"{board.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The following will be deleted:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <Typography component="li" variant="body2" color="text.secondary">All square claims and positions</Typography>
            <Typography component="li" variant="body2" color="text.secondary">All participants and managers</Typography>
            <Typography component="li" variant="body2" color="text.secondary">All offline players assigned to this board</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Board settings and configuration</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

SettingsPanel.displayName = 'SettingsPanel';

export default SettingsPanel;
